/**
 * Landing page — single source of truth for every user-facing word on `/`,
 * `/audit/[domain]`, `/pricing`, `/report/sample`. Components import from
 * here; nothing gets inlined in JSX, so banned-word scans and brand audits
 * are a single grep across one file.
 *
 * @eran Copy contract. Production reuses these constants verbatim or maps
 * them to a CMS. If you swap to a CMS, the shapes below are the schema.
 *
 * Voice: `tookii-brand` skill (senior-PM register, banned-word list) +
 * `human-voice` skill (long-form anti-slop rules).
 */

// ---------------------------------------------------------------------------
// Top nav (v1.2.1 iteration 1, Direction D — shrink-to-pill on scroll).
// Pure copy; the visual states live in `<LandingTopNav>`.
// ---------------------------------------------------------------------------

export const topNavCopy = {
  links: [
    { href: '/#capabilities', label: 'Features' },
    { href: '/#how-it-works', label: 'How it works' },
    { href: '/blog', label: 'Blog' },
    { label: 'Pricing', tooltip: 'Coming soon' },
  ],
  signInLabel: 'Sign in',
  letsTalkLabel: "Let's talk",
  /** Demo-booking calendar — opens in a new tab from the signed-out nav CTA. */
  letsTalkUrl: 'https://calendar.app.google/8xiod623n7GJTtak7',
  signedInDashboardLabel: 'Dashboard',
} as const

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

export interface HeroCopy {
  /**
   * H1 — locked POV-frame pair, rendered as two lines. Shape is
   * `{ top, bottom }` so each line can break independently of
   * viewport-driven wrapping. v1.2 landed on the POV frame after a team
   * review round against five alternates (see git history for the
   * `HeroVariantPicker` scaffold that drove the comparison).
   */
  h1: { top: string; bottom: string }
  /**
   * H2 — concept-forward subhead. Names what Tookii generates (AI-powered
   * users from your target audience) and the three things you can do with
   * them (interview, observe, challenge). Closes with the time-compression
   * beat ("minutes, not weeks").
   */
  h2: string
  /** Trust line under the URL input. Short, senior-PM register. */
  trustLine: string
  /**
   * Variant H1 used on `/audit/[domain]`. Two-line like `h1`. `{domain}`
   * is a literal token the route replaces at render time — can appear in
   * either `top` or `bottom`.
   */
  h1DomainVariant: { top: string; bottom: string }
  /**
   * v1.1 — placeholder for the hero URL input. Reads as a real example
   * domain, not an apology ("e.g., your-site.com").
   */
  urlInputPlaceholder: string
  /**
   * v1.1 — submit-button label on the hero URL input. Direct verb, two
   * words, matches the v1 fake-CTA register.
   */
  urlInputCta: string
}

export const heroCopy: HeroCopy = {
  h1: { top: 'See your product', bottom: "through your users' eyes." },
  h2: 'Tookii generates AI-powered users from your target audience, so you can interview them, observe them, and validate every product decision — in minutes, not weeks.',
  trustLine: 'No credit card required.',
  h1DomainVariant: { top: 'Test {domain}', bottom: 'with evidence, not guesses.' },
  urlInputPlaceholder: 'your-site.com',
  urlInputCta: 'Meet your users',
}

// ---------------------------------------------------------------------------
// "How it works" — 5-step flow
// ---------------------------------------------------------------------------

export interface HowItWorksStepCopy {
  /** Stable id for React keys and analytics. */
  slug: string
  /** UPPERCASE-mono label above the title, e.g. "Step 1". */
  label: string
  /** Step headline. */
  title: string
  /** One-sentence body describing what happens in this step. */
  body: string
}

export interface HowItWorksCopy {
  eyebrow: string
  title: string
  intro: string
  /** Exactly 5 steps. */
  steps: readonly HowItWorksStepCopy[]
}

export const howItWorksCopy: HowItWorksCopy = {
  eyebrow: 'How it works',
  title: 'The new era of agentic product research',
  intro:
    'Synthetic users trained on your ICP show you what real users would do — with your product today, or with your next idea before you build it.',
  steps: [
    {
      slug: 'define',
      label: 'Step 1',
      title: 'Define what to test',
      body: 'Hand Tookii what you want to validate. No SDK, no integration — the test runs from the outside in, the way a real user would experience it.',
    },
    {
      slug: 'meet-users',
      label: 'Step 2',
      title: 'Meet your synthetic users',
      body: 'Tookii generates synthetic users from your ICP — real jobs-to-be-done, specific goals. Edit any of them, drop the ones that don’t fit, or add your own before they run.',
    },
    {
      slug: 'watch-use',
      label: 'Step 3',
      title: 'Watch them use your product',
      body: 'Each one navigates your product in a real browser — reads copy, tries flows, reacts to friction. Every step is captured as a session replay you can see.',
    },
    {
      slug: 'synthesize',
      label: 'Step 4',
      title: 'Synthesize findings',
      body: 'Tookii clusters the moments and links each one back to evidence — replay timestamps, persona quotes, screenshot frames.',
    },
    {
      slug: 'decide',
      label: 'Step 5',
      title: 'Decide what to ship next',
      body: 'A structured set of findings lands in minutes — what worked, what broke, and which moments are worth showing the team.',
    },
  ],
}

// ---------------------------------------------------------------------------
// "See the output" section — what the report contains, why it is different
// ---------------------------------------------------------------------------

export interface SeeTheOutputCopy {
  eyebrow: string
  title: string
  body: string
  /** Three short feature points — not a three-item rhetorical list, actual product features. */
  featurePoints: readonly string[]
  ctaLabel: string
  /** Single-line tooltip text shown below the CTA when clicked — matches the pricing nav tooltip pattern. */
  comingSoonTooltip: string
}

export const seeTheOutputCopy: SeeTheOutputCopy = {
  eyebrow: 'The output',
  title: 'Evidence, not summaries.',
  body: 'Every finding is a moment from the replay — the persona, the click, the reason they stalled or stuck. You watch it happen, scrub back, and decide what to ship next.',
  featurePoints: [
    'Session replay — every persona’s run captured as video you can scrub.',
    'Per-persona narratives — what they expected, what they saw, why they reacted.',
    'Stuck-here / loved-this stickies — the moments worth sharing with the team.',
  ],
  ctaLabel: 'Sample session',
  comingSoonTooltip: 'Sample session coming soon',
}

// ---------------------------------------------------------------------------
// FAQ — six cards, each defuses a specific PM or founder objection
// ---------------------------------------------------------------------------

export interface FaqCard {
  /** Question — the actual PM question, not a marketing paraphrase. */
  question: string
  /**
   * Answer — 2–4 short sentences. No three-item lists. No hedging. Lead with
   * a specific mechanism, end with a concrete consequence.
   */
  answer: string
}

export const faqCopy: readonly FaqCard[] = [
  {
    question: "Why shouldn't I build this myself with Claude?",
    answer:
      'The prompts are the cheap part. Calibration, controlled agents, task orchestration, and trace-level evidence are what move synthetic answers from roleplay to reliable — four years of academic work on LLMs as research respondents has proved it, and every credible synthetic-research product has rediscovered the same. Tookii runs that loop.',
  },
  {
    question: "I'm solo or on a small team. Is this still for me?",
    answer:
      "Yes — arguably more than if you had a research team. When you're shipping alone, every decision compounds. Cold outreach to find your first real users is its own full-time job. Tookii gives you synthetic users of your ICP in minutes — interview them about pricing, stress-test a landing page, or walk them through onboarding before a real user touches it.",
  },
  {
    question: 'What exactly is a persona, and how does Tookii build one?',
    answer:
      "A persona is a synthetic user — an AI agent with goals, pains, prior context, and a reason to be on your site. We don't generate them from a prompt. Before any persona exists, Tookii reads your site, product surfaces, and competitor positioning to build a working model of your business — who finds you, what they want, what alternatives they considered. The personas come out of that model. Each one drives a real browser through your product, and you can enrich any persona with your own context — past interviews, support tickets, ICP notes — to sharpen them against your real-world ICPs.",
  },
  {
    question: 'Does the agent actually use my product, or just read the HTML?',
    answer:
      'It uses the product. Each persona drives a real browser — follows links, fills forms, waits for pages to load. The session replay is a video. You see the cursor, the clicks, and the moments the persona gives up.',
  },
  {
    question: 'How do I trust the findings?',
    answer:
      'Every finding cites the persona, the step, and the reasoning. Hover any finding and you get the full trace — what the persona expected, what they saw, and why they reacted. If a finding does not hold up under the trace, you discard it.',
  },
  {
    question: 'Is this meant to replace traditional user research?',
    answer:
      "No, and we don't want to. Traditional research is still the way to learn motivation, willingness to pay, and trust. What Tookii does is make that research much faster, and sharper so you can de-risk decisions.",
  },
] as const

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

export type FooterLink =
  | {
      label: string
      href: string
      /** Open in a new tab (used for both external links and internal legal pages). */
      external?: boolean
    }
  | {
      /** Tooltip-only entry — clicking opens a small popup instead of routing. */
      label: string
      tooltip: string
    }

export interface FooterCopy {
  /** Final conversion CTA at the bottom of /. */
  finalCta: {
    title: string
    ctaLabel: string
  }
  /** Product links — routes inside the app, or tooltip-only entries. */
  productLinks: readonly FooterLink[]
  /** Legal + company links. */
  companyLinks: readonly FooterLink[]
  /** Copyright line. */
  copyright: string
}

export const footerCopy: FooterCopy = {
  finalCta: {
    title: 'Run your first test.',
    ctaLabel: 'Start now',
  },
  productLinks: [
    { label: 'Features', href: '/#capabilities' },
    { label: 'How it works', href: '/#how-it-works' },
    { label: 'FAQ', href: '/#faq' },
    { label: 'Blog', href: '/blog' },
    { label: 'Pricing', tooltip: 'Coming soon' },
  ],
  companyLinks: [
    { label: 'Terms', href: '/terms', external: true },
    { label: 'Privacy', href: '/privacy', external: true },
    { label: 'Security', href: '/security', external: true },
  ],
  copyright: '© 2026 Tookii. All rights reserved.',
}

// ---------------------------------------------------------------------------
// Pricing (feature-flagged; full Phase 5 work, strings seeded here for audit)
// ---------------------------------------------------------------------------

export interface PricingTier {
  name: string
  /** One-line framing for the tier. */
  tagline: string
  /** Price display — "Free", "TBD", "Contact". Phase 5 picks final values. */
  price: string
  /** What the tier enables, concrete. */
  features: readonly string[]
  ctaLabel: string
}

export const pricingCopy: readonly PricingTier[] = [
  {
    name: 'Free',
    tagline: 'Run one test every 7 days. See a partial report.',
    price: 'Free',
    features: [
      '1 test per 7 days',
      'Up to 3 personas per test',
      'Partial report — top finding, first persona',
      'Session replay for the first persona',
    ],
    ctaLabel: 'Start free',
  },
  {
    name: 'Pro',
    tagline: 'Unlimited tests. Full reports. For the PM shipping weekly.',
    price: 'TBD / month',
    features: [
      'Unlimited tests',
      'Up to 10 personas per test',
      'Full report — every persona, every finding',
      'Export to PDF and share links',
      'Test history and diffs across runs',
    ],
    ctaLabel: 'Upgrade to Pro',
  },
  {
    name: 'Team',
    tagline: 'Shared workspaces, SSO, and test-review workflows.',
    price: 'Contact us',
    features: [
      'Everything in Pro',
      'Shared workspaces and roles',
      'SSO (Google, Okta)',
      'Test-review comments and approvals',
      'Priority support',
    ],
    ctaLabel: 'Talk to us',
  },
] as const

// ---------------------------------------------------------------------------
// Fake upgrade CTAs — labels only. Event shape lives in Phase 5.
// ---------------------------------------------------------------------------

export type FakeCtaReason =
  | 'more-audits'
  | 'team-features'
  | 'api-access'
  | 'priority-support'
  | 'capability-waitlist'

export const fakeCtaCopy: Record<FakeCtaReason, { label: string; modalTitle: string; modalBody: string }> = {
  'more-audits': {
    label: 'Run more tests',
    modalTitle: 'More tests are coming soon.',
    modalBody:
      'Your free tier includes one test every 7 days. Leave your email and we will tell you when Pro opens.',
  },
  'team-features': {
    label: 'Add your team',
    modalTitle: 'Team workspaces are coming soon.',
    modalBody:
      'Shared workspaces, roles, and review workflows land with the Team plan. Leave your email to be first in.',
  },
  'api-access': {
    label: 'Get API access',
    modalTitle: 'API access is coming soon.',
    modalBody:
      'Kick off tests from your CI or Linear workflow. Leave your email and we will reach out.',
  },
  'priority-support': {
    label: 'Get priority support',
    modalTitle: 'Priority support is a Team plan feature.',
    modalBody:
      'Direct-line support with a human who has read your test. Leave your email and we will be in touch.',
  },
  // v1.1 — generic default. Capability grid tiles pass `modalTitle` / `modalBody`
  // overrides per-tile so the copy carries the capability name. This fallback
  // only renders if a caller forgets the override.
  'capability-waitlist': {
    label: 'Join the waitlist',
    modalTitle: 'Joining the waitlist.',
    modalBody:
      'We will email you when this capability ships. One message, no marketing sequence.',
  },
} as const

// ---------------------------------------------------------------------------
// v1.1 — Capability grid (6 pillars, 1 Live + 5 Coming)
// ---------------------------------------------------------------------------

/**
 * Single pillar in the `<CapabilityGrid>` — advertises one surface of the
 * platform. `state` governs the badge ("Live now" vs "Coming soon") only;
 * iteration 1 dropped per-tile CTAs so cards are purely informational.
 *
 * @eran The `slug` is a stable React key + analytics anchor. Keep it
 * kebab-case if you ever re-introduce per-tile CTAs that emit events
 * (`context: 'capability-grid/<slug>'` was the prior contract).
 */
export interface CapabilityCopy {
  /** Kebab-case id — stable React key + analytics anchor. */
  slug: string
  /** Mono eyebrow — sequential tile number (01 / 02 / ...). */
  label: string
  /** Serif tile title. */
  title: string
  /** Body. Concrete mechanism + outcome. */
  body: string
  /** Delivery state. Drives only the "Live now" / "Coming soon" badge. */
  state: 'live' | 'coming'
}

/** Section eyebrow + title + intro for the grid header. */
export interface CapabilityGridCopy {
  eyebrow: string
  title: string
  intro: string
  pillars: readonly CapabilityCopy[]
}

export const capabilityGridCopy: CapabilityGridCopy = {
  eyebrow: 'What Tookii does',
  title: 'The validation layer for everything you ship.',
  intro:
    'Tookii turns synthetic users into a repeatable validation loop. Ask questions, watch behavior, test ideas before you build, and decide what to ship next.',
  pillars: [
    {
      slug: 'synthetic-users',
      label: '01',
      title: 'Synthetic Users',
      body: 'Synthetic users grounded in your ICP. Tookii builds synthetic users from your business, your industry, and your competitors, and you can then edit, refine, or add users of your own. The foundation every other test runs on.',
      state: 'live',
    },
    {
      // Slug stays `session-replay` (stable analytics anchor + image asset
      // path); user-visible label renamed "Session Replay" → "First Impression"
      // (2026-05-18). "Session replay" is preserved only where it names the
      // technical playback artifact, not the test type.
      slug: 'session-replay',
      label: '02',
      title: 'First Impression',
      body: 'The closest thing to watching real users — without the scheduling, recruiting, or waiting. Personas walk through your flow in a real browser. You watch every click, hesitation, and rage-quit, with the reasoning behind each one.',
      state: 'live',
    },
    {
      slug: 'ab-tests',
      label: '03',
      title: 'A/B Tests',
      body: 'Run the test before you build it. Show two variants to the same personas, see which one wins, and skip the dev cycles that end in inconclusive results.',
      state: 'live',
    },
    {
      slug: 'focus-group',
      label: '04',
      title: 'Focus Group',
      body: 'The conversation you’d kill to have before you ship. Drop your idea in front of the people you’re building for and find out where it lands — what they love, what loses them, and the objection you didn’t see coming.',
      state: 'live',
    },
    {
      // Slug `journey-test` is the stable analytics anchor; user-visible label
      // "Journey Test" (the multi-screen flow test — route `/dashboard/tests`
      // with `mode: 'jt'`). Replaced the "What to Build Next" coming-soon
      // placeholder 2026-07-25 when the flow test type shipped. Ordered ahead
      // of the remaining coming-soon tile so live cards lead and the
      // "Coming soon" card (Competitive Landscape) stays last.
      slug: 'journey-test',
      label: '05',
      title: 'Journey Test',
      body: 'See exactly where your flow loses people. Send your personas through the whole path — onboarding, signup, checkout — and watch, screen by screen, where each one clears, stalls, or drops. The compounding friction a single-screen test misses.',
      state: 'live',
    },
    {
      // Goal Test: the user types one goal ("book a demo") and each persona
      // pursues it in a real browser — forms included. Ships behind the
      // PostHog flag `goal-test` (src/features/tests/goal/flags.ts): the
      // dashboard and the chooser drop this slug when the flag is off, so the
      // shared copy stays flag-free. Route `/dashboard/tests` with `mode: 'gt'`.
      slug: 'goal-test',
      label: '06',
      title: 'Goal Test',
      body: 'Set the goal — book a demo, find the price, sign up — and watch each persona try to reach it in a real browser. Who gets there, who gives up, and the exact screen where it happened.',
      state: 'live',
    },
    {
      slug: 'competitive-landscape',
      label: '07',
      title: 'Competitive Landscape',
      body: 'Your personas don’t just know your product — they know the competitors you’re up against. Their positioning, their pricing, their reviews. Understand when would they pick you, what’s pulling them toward an alternative, and what would change their mind. The "why we lose deals" answer, on demand.',
      state: 'coming',
    },
  ],
} as const

// ---------------------------------------------------------------------------
// Dynamic outbound route — /audit/[domain]
// ---------------------------------------------------------------------------

export interface DomainRouteCopy {
  /** Mono label shown while we pretend to fetch the domain's context. `{domain}` is replaced at render time. */
  fetchingLabel: string
  /** Mono label shown after the mock fetch settles. `{domain}` is replaced at render time. */
  readyLabel: string
  /** Short line reinforcing the "tailored for your site" framing under the preview panel. */
  preparingBody: string
}

export const domainRouteCopy: DomainRouteCopy = {
  fetchingLabel: 'Fetching preview for {domain}',
  readyLabel: 'Preview ready for {domain}',
  preparingBody:
    'We are pulling your site context, competitor set, and initial personas. Start the test when you are ready.',
}

export interface AuditNotFoundCopy {
  eyebrow: string
  title: string
  body: string
  ctaLabel: string
  ctaHref: string
  secondaryLabel: string
  secondaryHref: string
}

export const auditNotFoundCopy: AuditNotFoundCopy = {
  eyebrow: '404 · Bad URL',
  title: 'That does not look like a domain we can test.',
  body: 'The URL needs a real hostname — like example.com or app.acme.io. Head back to the landing page and paste any site into the URL field.',
  ctaLabel: 'Go to the landing page',
  ctaHref: '/',
  secondaryLabel: 'See a sample report',
  secondaryHref: '/report/sample',
}

// ---------------------------------------------------------------------------
// Metadata — default strings that `buildMetadata` consumes
// ---------------------------------------------------------------------------

export const metadataCopy = {
  /** Default site title — used when a route does not override. */
  siteTitle: 'Tookii — Continuous product intelligence',
  /** Default meta description. */
  siteDescription:
    'Synthetic users run user research end-to-end on your product. Test a flow, get findings with reasoning, ship with evidence.',
  /** OG image alt. */
  ogImageAlt: 'Tookii — continuous product intelligence for product teams.',
} as const
